import * as React from 'react';
import { createContext, useContext, useState, useMemo, useRef, useCallback, useEffect } from 'react';
import Fuse, { type IFuseOptions } from 'fuse.js';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Command, CommandList, CommandItem, CommandEmpty } from 'shared/src/components/ui';
import { cn } from 'shared/src/utils';
import { useDebounce } from 'react-use';

interface FuzzySearchContextValue<T> {
  items: T[];
  filteredItems: T[];
  selectedItems: T[];
  value: string[];
  query: string;
  debouncedQuery: string;
  isOpen: boolean;
  selectedTagIndex: number | null;
  placeholder: string;
  emptyMessage: string;
  disabled: boolean;
  getItemId: (item: T) => string;
  getItemLabel: (item: T) => string;
  setQuery: (query: string) => void;
  setIsOpen: (open: boolean) => void;
  setSelectedTagIndex: (index: number | null) => void;
  select: (item: T) => void;
  remove: (id: string) => void;
  clearAll: () => void;
}

const FuzzySearchContext = createContext<FuzzySearchContextValue<unknown> | null>(null);

export function useFuzzySearch<T>() {
  const context = useContext(FuzzySearchContext);
  if (!context) {
    throw new Error('useFuzzySearch must be used within a FuzzySearchProvider');
  }
  return context as FuzzySearchContextValue<T>;
}

export type FuzzySearchPreviewGroupFields = {
  type?: string;
  tokenType?: string;
};

export interface FuzzySearchProviderProps<T extends FuzzySearchPreviewGroupFields> {
  children: React.ReactNode;
  items: T[];
  searchKeys: string[];
  getItemId: (item: T) => string;
  getItemLabel: (item: T) => string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  fuseOptions?: IFuseOptions<T>;
  onSearch?: (query: string) => void;
}

export function FuzzySearchProvider<T extends FuzzySearchPreviewGroupFields>({
  children,
  items,
  searchKeys,
  getItemId,
  getItemLabel,
  value,
  onChange,
  placeholder = 'Search...',
  emptyMessage = 'No results found',
  disabled = false,
  fuseOptions,
  onSearch,
}: FuzzySearchProviderProps<T>) {
  const [query, setQueryState] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTagIndex, setSelectedTagIndex] = useState<number | null>(null);
  const [filteredItems, setFilteredItems] = useState<T[]>([]);

  useDebounce(() => setDebouncedQuery(query), 150, [query]);

  useEffect(() => {
    onSearch?.(debouncedQuery);
  }, [debouncedQuery, onSearch]);

  const setQuery = useCallback((newQuery: string) => {
    setQueryState(newQuery);
  }, []);

  const fuse = useMemo(() => {
    return new Fuse(items, {
      keys: searchKeys,
      threshold: 0.4,
      ...fuseOptions,
    });
  }, [items, searchKeys, fuseOptions]);

  useEffect(() => {
    if (debouncedQuery.trim()) {
      setFilteredItems(fuse.search(debouncedQuery, { limit: 50 }).map((r) => r.item));
      return;
    }

    const previewPerGroup = 50;
    const maxPreviewTotal = 200;

    const byType: Record<string, T[]> = {};
    for (const item of items) {
      const groupKey = item.type ?? item.tokenType ?? 'default';

      if (!byType[groupKey]) byType[groupKey] = [];
      if (byType[groupKey].length < previewPerGroup) byType[groupKey].push(item);
    }

    setFilteredItems(Object.values(byType).flat().slice(0, maxPreviewTotal));
  }, [debouncedQuery, items, fuse]);

  const selectedItems = useMemo(() => {
    return items.filter((item) => value.includes(getItemId(item)));
  }, [items, value, getItemId]);

  useEffect(() => {
    reset(value);
  }, [value]);

  const select = useCallback(
    (item: T) => {
      const id = getItemId(item);
      value.includes(id) ? onChange(value.filter((v) => v !== id)) : onChange([...value, id]);
      setQuery('');
    },
    [value, onChange, getItemId, setQuery],
  );

  const remove = useCallback(
    (id: string) => {
      onChange(value.filter((v) => v !== id));
    },
    [value, onChange],
  );

  const clearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  function reset(_value: string[]) {
    setSelectedTagIndex(null);
  }

  const contextValue: FuzzySearchContextValue<T> = {
    items,
    filteredItems,
    selectedItems,
    value,
    query,
    debouncedQuery,
    isOpen,
    selectedTagIndex,
    placeholder,
    emptyMessage,
    disabled,
    getItemId,
    getItemLabel,
    setQuery,
    setIsOpen,
    setSelectedTagIndex,
    select,
    remove,
    clearAll,
  };

  return <FuzzySearchContext.Provider value={contextValue as FuzzySearchContextValue<unknown>}>{children}</FuzzySearchContext.Provider>;
}

export interface FuzzySearchInputProps<T> {
  children?: React.ReactNode;
  className?: string;
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode;
}

export function FuzzySearchInput<T>({ children, className, renderItem }: FuzzySearchInputProps<T>) {
  const {
    filteredItems,
    value,
    query,
    debouncedQuery,
    isOpen,
    selectedTagIndex,
    placeholder,
    emptyMessage,
    disabled,
    getItemId,
    getItemLabel,
    setQuery,
    setIsOpen,
    setSelectedTagIndex,
    select,
    remove,
    selectedItems,
  } = useFuzzySearch<T>();

  const hasInlineChildren = React.Children.count(children) > 0;

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsOpen]);

  useEffect(() => {
    resetHighlightedItem(debouncedQuery, filteredItems);
  }, [debouncedQuery, filteredItems]);

  useEffect(() => {
    const element = itemRefs.current.get(highlightedIndex);
    if (element && isOpen) {
      const parent = element.closest('[cmdk-list]');
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        if (elementRect.bottom > parentRect.bottom) {
          parent.scrollTop += elementRect.bottom - parentRect.bottom;
        } else if (elementRect.top < parentRect.top) {
          parent.scrollTop -= parentRect.top - elementRect.top;
        }
      }
    }
  }, [highlightedIndex, isOpen]);

  function resetHighlightedItem(_query: string, _filteredItems: T[]) {
    setHighlightedIndex(0);
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const cursorAtStart = inputRef.current?.selectionStart === 0 && inputRef.current?.selectionEnd === 0;

    if (selectedTagIndex !== null && hasInlineChildren) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (selectedTagIndex > 0) {
          setSelectedTagIndex(selectedTagIndex - 1);
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (selectedTagIndex < selectedItems.length - 1) {
          setSelectedTagIndex(selectedTagIndex + 1);
        } else {
          setSelectedTagIndex(null);
          inputRef.current?.focus();
        }
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        const itemToRemove = selectedItems[selectedTagIndex];
        if (itemToRemove) {
          const newIndex = selectedTagIndex > 0 ? selectedTagIndex - 1 : null;
          remove(getItemId(itemToRemove));
          setSelectedTagIndex(selectedItems.length > 1 ? newIndex : null);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedTagIndex(null);
      } else if (e.key.length === 1) {
        setSelectedTagIndex(null);
      }
      return;
    }

    if (e.key === 'ArrowLeft' && cursorAtStart && hasInlineChildren && selectedItems.length > 0) {
      e.preventDefault();
      setSelectedTagIndex(selectedItems.length - 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setHighlightedIndex(Math.min(highlightedIndex + 1, filteredItems.length - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(Math.max(highlightedIndex - 1, 0));
    } else if (e.key === 'Enter' && isOpen && filteredItems[highlightedIndex]) {
      e.preventDefault();
      select(filteredItems[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Backspace' && query === '' && hasInlineChildren && selectedItems.length > 0) {
      e.preventDefault();
      setSelectedTagIndex(selectedItems.length - 1);
    }
  };

  const defaultRenderItem = (item: T, isSelected: boolean) => <span className={cn('truncate', isSelected && 'font-medium')}>{getItemLabel(item)}</span>;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div
        className={cn(
          'flex items-center flex-wrap min-h-8 gap-1 pl-4 py-1 border border-input rounded-md bg-background text-sm cursor-text',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        onClick={() => {
          if (!disabled) inputRef.current?.focus();
        }}
      >
        <MagnifyingGlassIcon className="size-3.5 shrink-0 text-muted-foreground" />
        {children}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedItems.length === 0 || !hasInlineChildren ? placeholder : ''}
          disabled={disabled}
          className="flex-1 min-w-[60px] bg-transparent border-none outline-none shadow-none ring-0 focus:ring-0 focus:outline-none text-sm placeholder:text-muted-foreground h-6"
          style={{ boxShadow: 'none' }}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 max-w-xl w-full bg-popover border rounded-md shadow-md overflow-hidden animate-in fade-in-0 zoom-in-95 duration-100">
          <Command shouldFilter={false} className="h-auto overflow-visible bg-transparent">
            <CommandList className="max-h-[240px]">
              {filteredItems.length === 0 && query.trim() && <CommandEmpty>{emptyMessage}</CommandEmpty>}
              {filteredItems.map((item, index) => {
                const id = getItemId(item);
                const isSelected = value.includes(id);
                const isHighlighted = index === highlightedIndex;
                return (
                  <CommandItem
                    key={id}
                    ref={(el) => {
                      if (el) itemRefs.current.set(index, el);
                      else itemRefs.current.delete(index);
                    }}
                    value={id}
                    onSelect={() => {
                      select(item);
                      inputRef.current?.focus();
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn('flex items-center gap-2 cursor-pointer', isHighlighted && 'bg-accent')}
                    data-highlighted={isHighlighted}
                  >
                    <div
                      className={cn(
                        'flex size-4 shrink-0 items-center justify-center rounded-sm border transition-colors',
                        isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30',
                      )}
                    >
                      {isSelected && <CheckIcon className="size-3" strokeWidth={3} />}
                    </div>
                    {renderItem ? renderItem(item, isSelected) : defaultRenderItem(item, isSelected)}
                  </CommandItem>
                );
              })}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}

export interface FuzzySearchResultsProps<T> {
  children: (item: T | null, onRemove: () => void, index: number, isSelected: boolean) => React.ReactNode;
}

export function FuzzySearchResults<T>({ children }: FuzzySearchResultsProps<T>) {
  const { selectedItems, selectedTagIndex, getItemId, remove, clearAll } = useFuzzySearch<T>();

  if (selectedItems.length === 0) return null;

  return (
    <>
      {selectedItems.map((item, index) => {
        const id = getItemId(item);
        const isSelected = selectedTagIndex === index;
        return <React.Fragment key={id}>{children(item, () => remove(id), index, isSelected)}</React.Fragment>;
      })}
      {selectedItems.length ? <React.Fragment>{children(null, clearAll, -1, false)}</React.Fragment> : null}
    </>
  );
}

export interface FuzzySearchClearAllProps {
  children?: (clearAll: () => void) => React.ReactNode;
  className?: string;
}

export function FuzzySearchClearAll({ children, className }: FuzzySearchClearAllProps) {
  const { selectedItems, clearAll } = useFuzzySearch();

  if (selectedItems.length < 2) return null;

  if (children) {
    return <>{children(clearAll)}</>;
  }

  return (
    <button type="button" onClick={clearAll} className={cn('text-xs text-muted-foreground hover:text-foreground transition-colors', className)}>
      Clear all
    </button>
  );
}

export interface FuzzySearchBadgeProps {
  children: React.ReactNode;
  onRemove: () => void;
  className?: string;
  isSelected?: boolean;
}

export function FuzzySearchBadge({ children, onRemove, className, isSelected }: FuzzySearchBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 bg-accent text-accent-foreground text-xs px-1.5 py-0.5 rounded transition-shadow',
        isSelected && 'ring-2 ring-primary ring-offset-1',
        className,
      )}
    >
      <span className="max-w-[120px] truncate">{children}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="hover:bg-muted-foreground/20 rounded-sm ml-0.5"
      >
        <XMarkIcon className="size-3.5" />
      </button>
    </span>
  );
}

export interface FuzzySearchSelectProps<T extends FuzzySearchPreviewGroupFields> {
  items: T[];
  searchKeys: string[];
  getItemId: (item: T) => string;
  getItemLabel: (item: T) => string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  fuseOptions?: IFuseOptions<T>;
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode;
  renderBadge?: (item: T | null, onRemove: () => void, isSelected: boolean) => React.ReactNode;
  onSearch?: (query: string) => void;
}

export function FuzzySearchSelect<T extends FuzzySearchPreviewGroupFields>({
  items,
  searchKeys,
  getItemId,
  getItemLabel,
  value,
  onChange,
  placeholder,
  emptyMessage,
  className,
  disabled,
  fuseOptions,
  renderItem,
  renderBadge,
  onSearch,
}: FuzzySearchSelectProps<T>) {
  return (
    <FuzzySearchProvider
      items={items}
      searchKeys={searchKeys}
      getItemId={getItemId}
      getItemLabel={getItemLabel}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      disabled={disabled}
      fuseOptions={fuseOptions}
      onSearch={onSearch}
    >
      <FuzzySearchInput className={className} renderItem={renderItem ? (item, isSelected) => renderItem(item as T, isSelected) : undefined}>
        <FuzzySearchResults<T>>
          {(item, onRemove, _index, isSelected) => {
            if (renderBadge) {
              return renderBadge(item, onRemove, isSelected);
            }

            if (item === null) {
              return (
                <button type="button" onClick={onRemove} className="text-xs text-muted-foreground hover:text-foreground transition-colors px-1">
                  Clear
                </button>
              );
            }

            return (
              <FuzzySearchBadge onRemove={onRemove} isSelected={isSelected}>
                {getItemLabel(item)}
              </FuzzySearchBadge>
            );
          }}
        </FuzzySearchResults>
      </FuzzySearchInput>
    </FuzzySearchProvider>
  );
}
